/**
 * Family Chat (§22.14) client — parent side.
 *
 * Conversations are keyed by child_id (one parent↔child thread per child).
 * Message kinds: text | quick_action | location | voice. The parent sends
 * text + quick actions (come_home / call_me) + location; the child app
 * (Flutter) handles im_okay + voice notes.
 */
import { api } from './client';

export type MessageKind = 'text' | 'quick_action' | 'location' | 'voice';
export type QuickAction = 'im_okay' | 'come_home' | 'call_me';

export interface ChatMessage {
  id: string;
  conversation_id: string;
  child_id: string;
  sender_type: 'parent' | 'child' | 'system';
  sender_user_id: string | null;
  kind: MessageKind;
  body: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
  read_at: string | null;
}

export interface Conversation {
  id: string;
  family_id: string;
  child_id: string;
  last_message_at: string | null;
  pinned_text: string | null;
  created_at: string;
  unread_count: number;
  last_message: ChatMessage | null;
}

export interface SendMessageInput {
  kind?: MessageKind;
  body?: string;
  action?: QuickAction;
  lat?: number;
  lng?: number;
}

export const chatApi = {
  listConversations: () => api<Conversation[]>('/v1/chat/conversations'),
  getMessages: (childId: string, limit = 100) =>
    api<ChatMessage[]>(`/v1/chat/conversations/${childId}/messages?limit=${limit}`),
  send: (childId: string, input: SendMessageInput) =>
    api<ChatMessage>(`/v1/chat/conversations/${childId}/messages`, {
      method: 'POST',
      body: input,
    }),
  markRead: (childId: string) =>
    api<void>(`/v1/chat/conversations/${childId}/read`, { method: 'POST' }),
  setPinned: (childId: string, pinned_text: string | null) =>
    api<Conversation>(`/v1/chat/conversations/${childId}/pin`, {
      method: 'PUT',
      body: { pinned_text },
    }),
};
